"use client";
import { getApiBaseUrl } from "@/lib/apiBase";
import { fetchJsonArray } from "@/lib/fetchJsonSafe";

import { useState, useEffect, useMemo } from "react";
import { ArrowRight, Star } from "lucide-react";
import Image from "next/image";
import Link from "next/link";

const BASE_URL = getApiBaseUrl();
/** Нэг мөрөнд оруулах хамгийн их ангиллын тоо (ангилал бүрээс 1 шилдэг) */
const MAX_CATEGORIES_IN_ROW = 12;

function companyImageSrc(image) {
  if (!image || typeof image !== "string") return "";
  const t = image.trim();
  if (!t || t === "null" || t === "undefined") return "";
  if (/^data:/i.test(t)) return t;
  if (/^https?:\/\//i.test(t)) return t;
  const path = t.startsWith("/") ? t : `/${t}`;
  return BASE_URL ? `${BASE_URL}${path}` : path;
}

function shouldUnoptimizeImage(src) {
  return typeof src === "string" && /^data:/i.test(src);
}

function averageRating(company) {
  const rev = company.reviews || [];
  const rated = rev.filter((r) => (r.rating || 0) > 0);
  if (!rated.length) return 0;
  return rated.reduce((s, r) => s + r.rating, 0) / rated.length;
}

function partnerIsVip(company) {
  return !!(company?.is_vip || company?.vip || company?.isVip || company?.isPaidListing);
}

function CompanyCard({ company, hideCategory, compact }) {
  const reviewCount = company.reviews ? company.reviews.length : 0;
  const [imageError, setImageError] = useState(false);
  const imgSrc = companyImageSrc(company.image);

  const avg = averageRating(company);
  const averageRatingLabel = avg > 0 ? avg.toFixed(1) : "N/A";

  const getStarSquareBackground = (rating) => {
    if (rating >= 5) return "bg-green-600";
    if (rating >= 4) return "bg-green-400";
    if (rating >= 3) return "bg-yellow-400";
    if (rating >= 2) return "bg-orange-400";
    return "bg-red-600";
  };

  const isVIP = partnerIsVip(company);
  const hasActivePackage =
    company.packages &&
    company.packages.length > 0 &&
    company.packages.some((pkg) => pkg.is_active);
  const isPremium = isVIP || hasActivePackage;

  return (
    <Link href={`/partner/${company.id}`}>
      <div
        className={`group relative flex h-full cursor-pointer flex-col overflow-hidden rounded-2xl bg-white p-0 ${
          isPremium
            ? "border-2 border-yellow-400 shadow-lg shadow-yellow-400/30"
            : "border border-slate-200"
        }`}
      >
        {isVIP && (
          <div
            className={`absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full border-2 border-white bg-gradient-to-r from-yellow-400 to-yellow-500 font-bold text-white shadow-lg ${
              compact ? "px-2 py-0.5 text-[10px]" : "left-3 top-3 gap-1.5 px-3 py-1.5 text-xs"
            }`}
          >
            <span>⭐ VIP</span>
          </div>
        )}
        {!isVIP && hasActivePackage && (
          <div
            className={`absolute left-2 top-2 z-20 flex items-center gap-1 rounded-full border-2 border-white bg-gradient-to-r from-blue-500 to-blue-600 font-bold text-white shadow-lg ${
              compact ? "px-2 py-0.5 text-[10px]" : "left-3 top-3 gap-1.5 px-3 py-1.5 text-xs"
            }`}
          >
            <span className="max-w-[5rem] truncate">📦 {company.packages[0]?.package?.name || "Package"}</span>
          </div>
        )}
        {reviewCount > 0 && (
          <div
            className={`absolute right-2 top-2 z-20 flex items-center gap-1 rounded-full border border-slate-200/50 bg-white/95 font-semibold text-slate-800 shadow-lg backdrop-blur-md ${
              compact ? "px-2 py-0.5 text-[10px]" : "right-3 top-3 gap-1.5 px-3 py-1.5 text-xs"
            }`}
          >
            <span>{reviewCount}</span>
            {!compact && <span className="hidden sm:inline">сэтгэгдэл</span>}
          </div>
        )}

        <div
          className={`relative w-full overflow-hidden bg-gray-100 ${compact ? "h-36" : "h-52 md:h-56"}`}
        >
          {imgSrc && !imageError ? (
            <Image
              src={imgSrc}
              alt={company.company_name}
              fill
              className="object-cover transition-transform duration-500 ease-out group-hover:scale-110"
              onError={() => setImageError(true)}
              unoptimized={shouldUnoptimizeImage(imgSrc)}
            />
          ) : (
            <div className="flex h-full w-full flex-col items-center justify-center bg-gray-100">
              <div className={`font-extrabold text-slate-400 ${compact ? "mb-1 text-4xl" : "mb-2 text-7xl"}`}>
                {company.company_name?.[0]?.toUpperCase() || "?"}
              </div>
              {!compact && (
                <div className="px-4 text-center text-xs font-medium text-slate-500">
                  {company.company_name || "Компани"}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={`flex flex-1 flex-col ${compact ? "p-3" : "p-4"}`}>
          <h3
            className={`line-clamp-2 font-bold text-slate-900 ${compact ? "mb-1.5 text-sm leading-snug" : "mb-2 text-base"}`}
          >
            {company.company_name}
          </h3>

          {!hideCategory && company.category?.name && (
            <div className="mb-2">
              <span className="inline-flex items-center rounded-full border border-[#577099]/20 bg-[#577099]/10 px-2 py-1 text-xs font-medium text-[#577099]">
                {company.category.name}
              </span>
            </div>
          )}

          <div className={`text-slate-600 ${compact ? "mb-0 text-xs" : "mb-3 text-sm"}`}>
            {averageRatingLabel !== "N/A" ? (
              <div className="flex items-center gap-1.5">
                <div className="flex items-center gap-0.5">
                  {[...Array(5)].map((_, i) => {
                    const rating = parseFloat(averageRatingLabel);
                    const isFilled = i < Math.round(rating);
                    const box = compact ? "h-3 w-3" : "h-4 w-4";
                    const ic = compact ? "h-2 w-2" : "h-2.5 w-2.5";
                    return (
                      <div
                        key={i}
                        className={`flex items-center justify-center rounded-sm ${box} ${
                          isFilled ? getStarSquareBackground(rating) : "bg-slate-200"
                        }`}
                      >
                        <Star
                          className={`${ic} ${
                            isFilled ? "fill-current text-white" : "fill-current text-slate-400"
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
                <span className={`font-bold text-slate-800 ${compact ? "text-xs" : "text-sm"}`}>
                  {averageRatingLabel}
                </span>
                {!compact && (
                  <span className="text-xs text-slate-500">({reviewCount})</span>
                )}
              </div>
            ) : (
              <span className="text-slate-400">—</span>
            )}
          </div>

          {!compact && (
          <div className="mt-auto space-y-1.5 border-t border-slate-100 pt-3">
            {company.company_phone && (
              <div className="flex items-center gap-1.5 truncate text-xs text-slate-600">
                <svg className="h-3.5 w-3.5 flex-shrink-0 text-[#577099]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                  />
                </svg>
                <span className="truncate">{company.company_phone}</span>
              </div>
            )}
            {company.company_email && (
              <div className="flex items-center gap-1.5 truncate text-xs text-slate-600">
                <svg className="h-3.5 w-3.5 flex-shrink-0 text-[#577099]" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M3 8l7.89 5.26a2 2 0 002.22 0L21 8M5 19h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z"
                  />
                </svg>
                <span className="truncate">{company.company_email}</span>
              </div>
            )}
          </div>
          )}
        </div>
      </div>
    </Link>
  );
}

function sortCompaniesInGroup(a, b) {
  const va = partnerIsVip(a);
  const vb = partnerIsVip(b);
  if (va !== vb) return va ? -1 : 1;
  const ra = averageRating(a);
  const rb = averageRating(b);
  if (rb !== ra) return rb - ra;
  return (b.reviews?.length || 0) - (a.reviews?.length || 0);
}

function groupScore(group) {
  const top = group.companies[0];
  return averageRating(top) * 100 + (top.reviews?.length || 0);
}

/** Ангилал бүрээс хамгийн сайн 1-ийг авч, бүгдийг нэг жагсаалтаар буцаана (нэг мөрний горизонталь гулгалт). */
function buildFlatTopOnePerCategory(companies) {
  const map = new Map();

  for (const c of companies) {
    const id = c.category?.id ?? "__other__";
    if (!map.has(id)) {
      map.set(id, { id, companies: [] });
    }
    map.get(id).companies.push(c);
  }

  for (const g of map.values()) {
    g.companies.sort(sortCompaniesInGroup);
  }

  const orderedGroups = Array.from(map.values())
    .filter((g) => g.companies.length > 0)
    .sort((a, b) => groupScore(b) - groupScore(a))
    .slice(0, MAX_CATEGORIES_IN_ROW);

  const seen = new Set();
  const flat = [];
  for (const g of orderedGroups) {
    const top = g.companies[0];
    if (top && !seen.has(top.id)) {
      seen.add(top.id);
      flat.push(top);
    }
  }
  return flat;
}

export default function TopCompanies() {
  const [companies, setCompanies] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchCompanies = async () => {
      try {
        setLoading(true);
        const raw = await fetchJsonArray(`/api/partner?type=partner`);
        setCompanies(raw);
      } catch (error) {
        console.error("Error fetching companies:", error);
        setCompanies([]);
      } finally {
        setLoading(false);
      }
    };
    fetchCompanies();
  }, []);

  const topRowCompanies = useMemo(() => buildFlatTopOnePerCategory(companies), [companies]);

  if (loading) {
    return (
      <div className="mx-auto mt-6 max-w-[1440px] px-4 py-8">
        <div className="mb-6 space-y-2">
          <div className="mb-2 flex items-center gap-3">
            <div className="h-1 w-8 animate-pulse rounded-full bg-slate-200" />
            <div className="h-3 w-32 animate-pulse rounded bg-slate-200" />
          </div>
          <div className="h-8 w-48 animate-pulse rounded-lg bg-slate-200 md:w-64" />
          <div className="h-3 max-w-md animate-pulse rounded bg-slate-200" />
        </div>
        <div className="flex gap-3 overflow-hidden">
          {[1, 2, 3, 4, 5, 6].map((j) => (
            <div key={j} className="h-[220px] w-[min(78vw,240px)] shrink-0 animate-pulse rounded-2xl bg-slate-100" />
          ))}
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto mt-6 max-w-[1440px] px-4 py-8">
      <div className="mb-6">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <div>
            <div className="mb-2 flex items-center gap-3">
              <div className="h-1 w-8 rounded-full bg-[#577099]" />
              <span className="text-xs font-medium uppercase tracking-wide text-blue-600">Онцлох компаниуд</span>
            </div>
            <h2 id="top-companies-heading" className="text-2xl font-bold text-slate-900 md:text-3xl">
              Топ компаниуд
            </h2>
            <p className="mt-1 text-sm text-slate-600">
              Ангилал бүрээс нэг шилдэгийг сонгож, нэг мөрөнд цуглуулсан. Жижиг дэлгэц дээр хэвтээ чиглэлд гулгаж үзнэ үү.
            </p>
          </div>
          <Link
            href="/partner/partnersearch?search=&district="
            className="flex shrink-0 items-center gap-2 rounded-full bg-[#577099] px-5 py-2.5 text-sm font-semibold text-white shadow-lg transition-all duration-300 hover:scale-[1.02] hover:bg-[#4a5f85] hover:shadow-xl md:px-6 md:py-3"
          >
            <span>Бүгдийг харах</span>
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </div>

      {topRowCompanies.length === 0 ? (
        <div className="py-12 text-center text-slate-400">Компани олдсонгүй</div>
      ) : (
        <section className="relative md:static" aria-labelledby="top-companies-heading">
          <div
            className="pointer-events-none absolute inset-y-0 left-0 z-10 w-10 bg-gradient-to-r from-white to-transparent md:hidden"
            aria-hidden
          />
          <div
            className="pointer-events-none absolute inset-y-0 right-0 z-10 w-10 bg-gradient-to-l from-white to-transparent md:hidden"
            aria-hidden
          />
          <div
            className="top-companies-scroll -mx-4 flex snap-x snap-mandatory gap-3 overflow-x-auto px-4 pb-2 pt-0.5 scroll-smooth md:mx-0 md:gap-4 md:px-0"
          >
            {topRowCompanies.map((company) => (
              <div
                key={company.id}
                className="w-[min(78vw,240px)] shrink-0 snap-start md:w-[220px] md:min-w-[220px]"
              >
                <CompanyCard company={company} hideCategory={false} compact />
              </div>
            ))}
            <div className="w-2 shrink-0 md:hidden" aria-hidden />
          </div>
        </section>
      )}
    </div>
  );
}
